import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { site, whatsappEnquiry } from '../data/site';
import { Icon, Ornament, Reveal } from './Primitives';

/* ---------------------------------------------------------- scroll restore */
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname]);
  return null;
}

/* ------------------------------------------------------------- breadcrumbs */
export function Breadcrumbs({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="text-[11px] uppercase tracking-wide2">
      <ol className="flex flex-wrap items-center gap-2 text-cream-200/55">
        {trail.map((item, i) => {
          const last = i === trail.length - 1;
          return (
            <li key={item.label} className="flex items-center gap-2">
              {item.to && !last ? (
                <Link to={item.to} className="hover:text-gold-300">
                  {item.label}
                </Link>
              ) : (
                <span className={last ? 'text-gold-300' : undefined}>{item.label}</span>
              )}
              {!last && <span aria-hidden="true" className="text-cream-200/30">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* ----------------------------------------------------------- interior hero */
/**
 * Navy banner used at the top of every interior page. `image` is optional —
 * without it the band is plain navy with a faint lattice.
 */
export function PageHero({ eyebrow, title, lede, image, trail, align = 'left' }) {
  return (
    <section className="noise-navy relative overflow-hidden">
      {image && (
        <>
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-royal-950 via-royal-900/85 to-royal-800/55"
            aria-hidden="true"
          />
        </>
      )}

      <div
        className={`shell relative pb-16 pt-14 sm:pb-20 sm:pt-16 ${
          align === 'center' ? 'text-center' : ''
        }`}
      >
        {trail && (
          <div className={align === 'center' ? 'flex justify-center' : ''}>
            <Breadcrumbs trail={trail} />
          </div>
        )}
        <Reveal className={`mt-7 ${align === 'center' ? 'mx-auto max-w-3xl' : 'max-w-3xl'}`}>
          {eyebrow && <p className="eyebrow-light">{eyebrow}</p>}
          <h1 className="display-xl mt-4 text-cream-50">{title}</h1>
          <Ornament
            tone="light"
            className={`mt-7 ${align === 'center' ? '' : '!justify-start'}`}
          />
          {lede && (
            <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-cream-200/75 sm:text-[17px]">
              {lede}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- cta band */
export function CtaBand({
  eyebrow = 'Visit Us',
  title = 'Come and try them on',
  text = 'Bridal suites are best chosen in person. Walk in on C G Road, or message us and we will set aside the pieces you have been looking at.',
}) {
  return (
    <section className="relative overflow-hidden bg-cream-100">
      <div className="lattice absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="shell relative py-16 sm:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="display-lg mt-4 text-royal-900">{title}</h2>
            <Ornament className="mt-6" />
            <p className="mx-auto mt-6 max-w-xl lede">{text}</p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <a
                href={whatsappEnquiry()}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-primary"
              >
                <Icon.WhatsApp className="h-4 w-4" />
                Message us
              </a>
              <Link to="/visit" className="btn-outline">
                Showroom &amp; directions
                <Icon.Arrow className="h-3.5 w-3.5" />
              </Link>
            </div>
            <p className="mt-7 text-[12.5px] text-royal-700/60">
              {site.addressOneLine} · {site.phone}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- floating contact */
/**
 * A single floating action. A stacked call button was also here, but two 52px
 * circles cover real content on a 390px screen — and the phone number is already
 * one tap away in the announcement bar and the mobile drawer.
 */
export function FloatingContact() {
  return (
    <a
      href={whatsappEnquiry()}
      target="_blank"
      rel="noreferrer noopener"
      aria-label="Enquire on WhatsApp"
      className="fixed bottom-5 right-5 z-30 grid h-[52px] w-[52px] place-items-center
        rounded-full bg-jade text-cream-50 shadow-lift ring-1 ring-cream-50/20
        transition-transform duration-500 ease-luxe hover:scale-105"
    >
      <Icon.WhatsApp className="h-6 w-6" />
    </a>
  );
}

/* ------------------------------------------------------------ value props */

/** Joins "C G Road" with non-breaking spaces so the address never splits lines. */
const keepAddressWhole = (s) => s.replace(/C G Road/g, 'C\u00A0G\u00A0Road');

export function AssurancePanel({ tone = 'cream' }) {
  /*
   * The four blurbs are written to within a few characters of each other so they
   * wrap to the same number of lines at any column width — otherwise one cell
   * runs to two lines and the row reads as ragged. "C G Road" is joined with
   * non-breaking spaces so the address never splits across a line.
   */
  const items = [
    {
      Glyph: Icon.Crown,
      title: `Since ${site.since}`,
      text: 'Three generations of the same family on C G Road.',
    },
    {
      Glyph: Icon.Diamond,
      title: 'Hallmarked gold',
      text: 'BIS-hallmarked, with purity and weight on every bill.',
    },
    {
      Glyph: Icon.Handshake,
      title: 'Chosen in person',
      text: 'Suites laid out on the counter, not picked off a screen.',
    },
    {
      Glyph: Icon.Ring,
      title: 'Made to order',
      text: 'Suites adapted, resized and commissioned to your design.',
    },
  ];

  const dark = tone === 'navy';

  return (
    <div
      className={`grid gap-px sm:grid-cols-2 lg:grid-cols-4 ${
        dark ? 'bg-cream-50/10' : 'bg-royal-800/10'
      }`}
    >
      {items.map(({ Glyph, title, text }, i) => (
        <Reveal
          key={title}
          delay={i * 70}
          className={`flex flex-col items-center px-6 py-9 text-center ${
            dark ? 'bg-royal-800' : 'bg-cream-50'
          }`}
        >
          <Glyph className={`h-7 w-7 ${dark ? 'text-gold-400' : 'text-gold-600'}`} />
          <h3
            className={`mt-4 font-display text-xl font-normal ${
              dark ? 'text-cream-50' : 'text-royal-900'
            }`}
          >
            {title}
          </h3>
          <p
            className={`mt-2 max-w-[17rem] text-[13px] leading-relaxed ${
              dark ? 'text-cream-200/65' : 'text-royal-700/65'
            }`}
          >
            {keepAddressWhole(text)}
          </p>
        </Reveal>
      ))}
    </div>
  );
}
